import { FC, useEffect, useRef, useState } from "react";
import styles from "./style.module.scss";

interface StateProps {
  onChange: (value: any) => void;
  value: number;
}

const Scale: FC<StateProps> = ({ onChange, value }) => {
  const [depth, setDepth] = useState(value);
  const sliderRef = useRef<HTMLInputElement>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const gradientStops = ["#4caf50", "#cddc39", "#ffc107", "#ff9800", "#f44336"];

  const updateBubblePosition = () => {
    const slider = sliderRef.current;
    const bubble = bubbleRef.current;
    if (slider && bubble) {
      const min = Number(slider.min);
      const max = Number(slider.max);
      const percent = ((depth - min) / (max - min)) * 100;
      const sliderWidth = slider.offsetWidth;
      const bubbleWidth = bubble.offsetWidth;

      const position = (percent / 100) * sliderWidth - bubbleWidth / 2;
      const adjusted = Math.min(
        Math.max(position, 0),
        sliderWidth - bubbleWidth
      );
      bubble.style.left = `${adjusted}px`;

      const selectedStops = gradientStops.slice(0, depth);
      const gradient = selectedStops.join(", ");
      slider.style.background = `linear-gradient(to right, ${gradient})`;
      slider.style.backgroundSize = `${value === 1 ? percent + 1.5 : percent}% 100%`;
      slider.style.backgroundRepeat = "no-repeat";
      slider.style.backgroundColor = "#e0e0e0";
    }
  };

  // Update on depth change
  useEffect(() => {
    updateBubblePosition();
  }, [depth]);

  // ✅ Watch for container resize (e.g., when AI panel opens)
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver(() => {
      updateBubblePosition();
    });

    observer.observe(container);

    // Also handle window resize fallback
    window.addEventListener("resize", updateBubblePosition);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateBubblePosition);
    };
  }, [depth]);

  const bubbleColor = gradientStops[depth - 1] || "#4caf50";

  return (
    <div className={styles.sliderContainer} ref={containerRef} data-testid="scale-container">
      <div className={styles.sliderWrapper}>
        <input
          ref={sliderRef}
          id="audit-depth"
          type="range"
          min="1"
          max="5"
          step="1"
          value={depth}
          data-testid="scale-slider"
          onChange={(e) => {
            const val = Number(e.target.value);
            setDepth(val);
            onChange(val);
          }}
        />
        <div
          ref={bubbleRef}
          className={styles.valueBubble}
          data-testid="scale-bubble"
          style={{
            backgroundColor: bubbleColor,
            ["--connector-color" as any]: bubbleColor,
          }}
        >
          {depth}
          <span className={styles.connector}></span>
        </div>
      </div>

      <div className={styles.sliderLabels}>
        <span>Shallow</span>
        <span>Descriptive</span>
        <span>Moderate</span>
        <span>Deeper</span>
        <span>Deepest</span>
      </div>
    </div>
  );
};

export default Scale;
